import { useEffect, useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import { SxProps, Theme } from "@mui/material/styles";

import Dialog from "components/dialog";
import PatientTasksV2Filters from "features/staff-tasks-v2/filters/staff-tasks-v2-filters.component";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";

import StaffTaskTable from "../staff-task-list-content/staff-task-table.component";

// Stable references to prevent unnecessary re-renders
const PAPER_CONTENT_SX: SxProps<Theme> = {
  width: "98vw",
  maxWidth: "none",
  height: "95vh",
  display: "flex",
  flexDirection: "column"
};

const DIALOG_CONTENT_SX: SxProps<Theme> = {
  boxSizing: "border-box",
  padding: "20px 20px !important",
  background: grey[100],
  overflowY: "auto",
  flex: 1,
  minHeight: 0
};

const DIALOG_TITLE_SX: SxProps<Theme> = { pl: "30px" };

export default function StaffTaskListModalDialog() {
  const {
    customFilters,
    taskFilters,
    selectedCustomFilters,
    setTaskFilters,
    updateDateRange,
    before_due_at,
    after_due_at,
    tasksList,
    totalTasksCount,
    isBusy,
    fetchMore,
    openTaskListModal,
    handleCloseTaskListModal,
    refetch
  } = useStaffTasksV2();

  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>([]);
  const [isSelectAllMode, setIsSelectAllMode] = useState(false);

  useEffect(() => {
    if (!openTaskListModal) {
      setSelectedTaskIds([]);
      setIsSelectAllMode(false);
    }
  }, [openTaskListModal]);

  useEffect(() => {
    setSelectedTaskIds([]);
    setIsSelectAllMode(false);
  }, [taskFilters]);

  // Stable reference for dialogContentProps to prevent re-renders
  const dialogContentProps = useMemo(() => ({ sx: DIALOG_CONTENT_SX }), []);

  return (
    <Dialog
      title="Clinic Tasks List"
      onClose={handleCloseTaskListModal}
      open={openTaskListModal}
      dialogTitleSx={DIALOG_TITLE_SX}
      minWidth={1200}
      maxWidth="xl"
      paperContentSx={PAPER_CONTENT_SX}
      dialogContentProps={dialogContentProps}
    >
      <Stack
        spacing={2}
        sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <PatientTasksV2Filters
          enableGrouping={false}
          customFilters={customFilters}
          currentFilters={taskFilters}
          setCurrentFilters={setTaskFilters}
          selectedCustomFilters={selectedCustomFilters}
          updateDateRange={updateDateRange}
          before_due_at={before_due_at}
          after_due_at={after_due_at}
          selectedTaskIds={selectedTaskIds}
          setSelectedTaskIds={setSelectedTaskIds}
          onRefreshData={refetch}
          isSelectAllMode={isSelectAllMode}
          totalFilteredCount={totalTasksCount}
        />
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
          Displaying {totalTasksCount} task{totalTasksCount !== 1 ? "s" : ""}
        </Typography>
        <Box mt={1} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <StaffTaskTable
            orderingType="clinic"
            tasks={tasksList}
            isLoading={isBusy}
            showPatientColumn
            onScroll={fetchMore}
            selectedTaskIds={selectedTaskIds}
            setSelectedTaskIds={setSelectedTaskIds}
            setIsSelectAllMode={setIsSelectAllMode}
            isSelectAllMode={isSelectAllMode}
          />
        </Box>
      </Stack>
    </Dialog>
  );
}
